import React from 'react';
import { Link } from 'react-router-dom';
import {
  Package,
  BookOpen,
  Receipt,
  Calculator,
  FileSpreadsheet,
  RefreshCw,
  Wallet,
  BarChart3,
  ClipboardCheck,
  Compass,
  CheckCircle,
  ArrowRight,
  Briefcase,
  Building,
  Globe,
  Layers,
  Users,
  Phone,
  MessageSquare,
  HelpCircle,
  Handshake,
  TrendingUp,
  Clock,
  Sparkles,
  Star,
  Crown,
  Info
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '../components/ui/accordion';
import usePageSeo from '../hooks/usePageSeo';
import JsonLd from '../components/JsonLd';
import { breadcrumbSchema, serviceSchema, faqSchema } from '../utils/seoSchemas';
import RelatedServices from '../components/RelatedServices';

const SMEBusinessServicesUAE = () => {
  usePageSeo({
    title: 'SME Business Package UAE | Accounting, Tax & Compliance',
    description:
      'All-in-one SME business package in UAE including accounting, VAT, Corporate Tax, reporting and compliance support. Book a free consultation with WPAA.',
    path: '/sme-business-services-uae'
  });

  const included = [
    { title: 'Accounting & Bookkeeping', icon: BookOpen },
    { title: 'VAT Return Filing & Compliance', icon: Receipt },
    { title: 'Corporate Tax Registration & Filing Support', icon: Calculator },
    { title: 'Financial Statements Preparation', icon: FileSpreadsheet },
    { title: 'Bank, Customer & Supplier Reconciliation', icon: RefreshCw },
    { title: 'Payroll & Expense Recording Support', icon: Wallet },
    { title: 'Management Reports', icon: BarChart3 },
    { title: 'Audit Readiness Support', icon: ClipboardCheck },
    { title: 'Business Advisory & Compliance Guidance', icon: Compass }
  ];

  const whoFor = [
    { text: 'UAE startups', icon: TrendingUp },
    { text: 'Small and medium businesses', icon: Briefcase },
    { text: 'Free Zone companies', icon: Globe },
    { text: 'Service-based businesses', icon: Building },
    { text: 'Companies with monthly transactions', icon: Layers },
    { text: 'Businesses looking for one trusted compliance partner', icon: Handshake }
  ];

  const benefits = [
    'One professional team',
    'Clear monthly support',
    'Better compliance control',
    'Cost-effective bundled pricing',
    'Easier communication',
    'Audit, Tax & Advisory support together'
  ];

  const process = [
    { step: '01', title: 'Initial Business Review', description: 'Understand your business model, transaction volume and compliance needs.' },
    { step: '02', title: 'Document Collection & System Setup', description: 'Secure document collection and Zoho Books / QuickBooks configuration.' },
    { step: '03', title: 'Monthly Bookkeeping', description: 'Accurate recording of all transactions on a monthly cadence.' },
    { step: '04', title: 'VAT & CT Compliance Review', description: 'Periodic VAT return filing and Corporate Tax readiness checks.' },
    { step: '05', title: 'Financial Reporting', description: 'Monthly P&L, Balance Sheet and Cash Flow with KPI insights.' },
    { step: '06', title: 'Ongoing Advisory Support', description: 'Continuous business advisory based on your evolving needs.' }
  ];

  const whyChooseUs = [
    'UAE-focused compliance expertise',
    'Zoho Books & QuickBooks support',
    'VAT and Corporate Tax knowledge',
    'Practical advisory for SMEs',
    'Transparent and competitive pricing',
    'Your Partner in Growth'
  ];

  const tiers = [
    {
      id: 'starter',
      name: 'Starter Package',
      tagline: 'For small businesses / low transactions',
      price: '750',
      priceLabel: 'Starting from',
      cycle: '/month',
      icon: Sparkles,
      accent: 'green',
      features: [
        'Up to 50 transactions/month',
        'Basic bookkeeping',
        'Monthly reports',
        'Email support'
      ],
      ctaLabel: 'Get Started',
      popular: false
    },
    {
      id: 'growth',
      name: 'Growth Package',
      tagline: 'For growing SMEs',
      price: '1,500',
      priceLabel: 'Starting from',
      cycle: '/month',
      icon: Star,
      accent: 'amber',
      features: [
        'Up to 150 transactions/month',
        'Bookkeeping + VAT support',
        'Financial statements',
        'Compliance review',
        'Priority support'
      ],
      ctaLabel: 'Choose Growth',
      popular: true
    },
    {
      id: 'premium',
      name: 'Premium Package',
      tagline: 'For established businesses',
      price: '3,000',
      priceLabel: 'Starting from',
      cycle: '/month',
      icon: Crown,
      accent: 'purple',
      features: [
        '150+ transactions',
        'Full accounting + VAT + CT readiness',
        'Advisory support',
        'Audit-ready reporting',
        'Dedicated account manager'
      ],
      ctaLabel: 'Talk to Expert',
      popular: false
    }
  ];

  const faqs = [
    { q: 'Is this package monthly or one-time?', a: 'It is fully flexible — we structure it monthly, quarterly, or annually based on your transaction volume and business needs.' },
    { q: 'Is VAT filing included?', a: 'Yes, VAT registration and quarterly return filing support are included in most package tiers. We confirm scope during the initial consultation.' },
    { q: 'Do you support Free Zone companies?', a: 'Yes. We support both UAE Mainland and all major Free Zone entities (DMCC, DIC, SAIF, Hamriyah, JAFZA, etc.).' },
    { q: 'Can you prepare accounts for audit?', a: 'Absolutely. We maintain audit-ready financial records and prepare all necessary statements and reconciliations to make your annual audit smooth.' },
    { q: 'Is Corporate Tax support included?', a: 'Yes. CT registration with the FTA, tax computation, and annual return filing support are all part of the package.' }
  ];

  return (
    <div className="min-h-screen">
      <JsonLd
        id="ld-sme-service"
        data={{
          ...serviceSchema({
            name: 'SME Business Package in UAE',
            description:
              'All-in-one accounting, VAT, Corporate Tax, financial reporting, audit readiness and advisory support package designed for UAE SMEs.',
            path: '/sme-business-services-uae',
            serviceType: 'SME Business Services'
          }),
          offers: {
            '@type': 'AggregateOffer',
            priceCurrency: 'AED',
            lowPrice: '750',
            highPrice: '3000',
            offerCount: 3,
            availability: 'https://schema.org/InStock'
          }
        }}
      />
      <JsonLd
        id="ld-sme-breadcrumb"
        data={breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Services', path: '/services' },
          { name: 'SME Business Package', path: '/sme-business-services-uae' }
        ])}
      />
      <JsonLd id="ld-sme-faq" data={faqSchema(faqs)} />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 bg-gradient-to-br from-amber-900 via-amber-800 to-gray-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-amber-400 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-amber-300 rounded-full filter blur-3xl"></div>
        </div>
        <div className="container mx-auto relative z-10">
          <div className="max-w-4xl">
            <div className="flex items-center space-x-2 mb-6">
              <Package className="text-amber-400" size={32} />
              <span className="text-amber-400 font-semibold text-sm uppercase tracking-wider">SME Business Package</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              SME Business Package in UAE
            </h1>
            <p className="text-2xl text-amber-200 mb-6 font-semibold">
              All-in-One Accounting, Tax & Compliance Support for SMEs
            </p>
            <p className="text-xl text-gray-300 mb-6 leading-relaxed max-w-3xl">
              Running an SME in the UAE requires more than basic bookkeeping. Businesses must manage accounting records, VAT compliance, Corporate Tax obligations, audit readiness, and ongoing advisory needs.
            </p>
            <p className="text-lg text-gray-400 mb-8 max-w-3xl">
              At White Paper Accounts Auditing (WPAA), our SME Business Package is designed to provide complete financial and compliance support under one trusted professional team.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-white px-8 py-6 text-lg" data-testid="sme-consultation-btn">
                  Book Free Consultation
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <a href="#pricing" data-testid="sme-pricing-btn">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg w-full">
                  View Pricing
                </Button>
              </a>
              <a href="tel:+971501219341" data-testid="sme-call-btn">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg w-full">
                  Call Us Now
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <span className="text-amber-600 font-semibold text-sm uppercase tracking-wider">Package Contents</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">What's Included in Our SME Business Package</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything an SME needs to stay compliant, organised and audit-ready — under one umbrella.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {included.map((item, index) => {
              const Icon = item.icon;
              return (
                <Card key={index} className="border-2 hover:border-amber-500 transition-all hover:shadow-lg">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="bg-amber-100 w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon className="text-amber-600" size={28} />
                    </div>
                    <div className="pt-2">
                      <h3 className="text-lg font-bold text-gray-900">{item.title}</h3>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who Is This Package For */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <span className="text-amber-600 font-semibold text-sm uppercase tracking-wider">Target Clients</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">Who Is This Package For?</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {whoFor.map((item, index) => {
              const Icon = item.icon;
              return (
                <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all">
                  <CardContent className="p-6 flex items-start space-x-4">
                    <div className="bg-amber-100 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="text-amber-600" size={24} />
                    </div>
                    <p className="text-gray-700 font-medium pt-2">{item.text}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Tiers */}
      <section className="py-20 px-4 bg-white" id="pricing">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <span className="text-amber-600 font-semibold text-sm uppercase tracking-wider">Transparent Pricing</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">SME Package Pricing</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Three flexible tiers built for UAE SMEs at every stage. Pay monthly, scale as you grow.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {tiers.map((tier) => {
              const Icon = tier.icon;
              const accentMap = {
                green: { iconBg: 'bg-green-100', iconText: 'text-green-600', priceText: 'text-green-700', btnBg: 'bg-green-600 hover:bg-green-700', borderActive: 'border-green-200' },
                amber: { iconBg: 'bg-amber-100', iconText: 'text-amber-600', priceText: 'text-amber-700', btnBg: 'bg-amber-600 hover:bg-amber-700', borderActive: 'border-amber-500' },
                purple: { iconBg: 'bg-purple-100', iconText: 'text-purple-600', priceText: 'text-purple-700', btnBg: 'bg-purple-600 hover:bg-purple-700', borderActive: 'border-purple-200' }
              };
              const a = accentMap[tier.accent];
              return (
                <div
                  key={tier.id}
                  data-testid={`pricing-tier-${tier.id}`}
                  className={`relative rounded-2xl bg-white border-2 ${tier.popular ? `${a.borderActive} shadow-2xl scale-105 lg:scale-110` : 'border-gray-200 shadow-sm hover:shadow-lg'} transition-all p-8 flex flex-col`}
                >
                  {tier.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <span className="bg-amber-500 text-white text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full shadow-md flex items-center gap-1">
                        <Star size={14} className="fill-current" />
                        Most Popular
                      </span>
                    </div>
                  )}

                  <div className={`${a.iconBg} w-14 h-14 rounded-xl flex items-center justify-center mb-5`}>
                    <Icon className={a.iconText} size={28} />
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{tier.name}</h3>
                  <p className="text-sm text-gray-500 mb-6 min-h-[2.5rem]">{tier.tagline}</p>

                  <div className="mb-6">
                    <p className="text-xs text-gray-500 mb-1">{tier.priceLabel}</p>
                    <div className="flex items-baseline gap-1">
                      <span className={`text-4xl font-bold ${a.priceText}`}>AED {tier.price}</span>
                      <span className="text-gray-500 text-sm">{tier.cycle}</span>
                    </div>
                  </div>

                  <ul className="space-y-3 mb-8 flex-1">
                    {tier.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-gray-700 text-sm">
                        <CheckCircle className={`${a.iconText} flex-shrink-0 mt-0.5`} size={18} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <Link to={`/contact?service=sme-${tier.id}`} data-testid={`pricing-cta-${tier.id}`}>
                    <Button className={`w-full ${a.btnBg} text-white py-6 text-base`}>
                      {tier.ctaLabel}
                    </Button>
                  </Link>
                </div>
              );
            })}
          </div>

          {/* Disclaimer */}
          <div className="max-w-3xl mx-auto mt-12">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 flex items-start gap-3">
              <Info className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
              <p className="text-sm text-amber-900 leading-relaxed">
                Final pricing may vary based on business activity, transaction volume, and specific compliance requirements. Book a free consultation to receive a tailored quote.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Bundled vs Separate */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div>
              <span className="text-amber-600 font-semibold text-sm uppercase tracking-wider">Smart Choice</span>
              <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">
                Why Choose an SME Package Instead of Separate Services?
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Managing multiple providers can be time-consuming and costly. With WPAA, you get:
              </p>
              <div className="space-y-3">
                {benefits.map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="text-amber-600 flex-shrink-0" size={22} />
                    <p className="text-gray-700">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-br from-amber-600 to-amber-700 rounded-2xl p-8 text-white">
              <Clock className="mb-4" size={48} />
              <h3 className="text-2xl font-bold mb-4">One Partner. Complete Coverage.</h3>
              <p className="text-amber-100 mb-6">
                Stop juggling separate accountants, tax consultants and advisors. WPAA brings everything under one professional team — saving you time, money, and compliance headaches.
              </p>
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-amber-500">
                <div>
                  <p className="text-3xl font-bold">18+</p>
                  <p className="text-sm text-amber-200">Years Experience</p>
                </div>
                <div>
                  <p className="text-3xl font-bold">200+</p>
                  <p className="text-sm text-amber-200">Clients Served</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Monthly Support Process */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <span className="text-amber-600 font-semibold text-sm uppercase tracking-wider">How We Work</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">Our Monthly Support Process</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {process.map((item, index) => (
              <div key={index} className="relative bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="absolute -top-4 -left-4 bg-amber-600 text-white w-14 h-14 rounded-xl flex items-center justify-center font-bold text-lg shadow-lg">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mt-4 mb-3">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose WPAA */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div>
              <span className="text-amber-600 font-semibold text-sm uppercase tracking-wider">Why WPAA</span>
              <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-8">Why Choose WPAA?</h2>
              <div className="space-y-4">
                {whyChooseUs.map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="text-amber-600 flex-shrink-0" size={24} />
                    <p className="text-lg text-gray-700">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-br from-amber-50 to-white border-2 border-amber-200 rounded-2xl p-8">
              <Users className="text-amber-600 mb-4" size={48} />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Built for UAE SMEs</h3>
              <p className="text-gray-700">
                Our SME package brings together everything a small or medium business needs — from day-to-day bookkeeping to year-end audit and Corporate Tax filing — at a price designed to fit growing companies.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <span className="text-amber-600 font-semibold text-sm uppercase tracking-wider">FAQs</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">Frequently Asked Questions</h2>
          </div>

          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-white border border-gray-200 rounded-xl px-6 shadow-sm"
                >
                  <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-amber-600 hover:no-underline">
                    <div className="flex items-start gap-3">
                      <HelpCircle className="text-amber-600 flex-shrink-0 mt-1" size={20} />
                      <span>{faq.q}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 text-base leading-relaxed pl-8">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <RelatedServices
        services={[
          { title: 'Accounting Services', description: "Standalone accounting if you don't need the full SME bundle.", path: '/accounting-services-uae', accent: 'indigo' },
          { title: 'Corporate Tax Services', description: '9% UAE Corporate Tax registration and filing — included or separate.', path: '/corporate-tax-uae', accent: 'teal' },
          { title: 'VAT Services', description: 'Independent VAT registration and filing services.', path: '/vat-services-uae', accent: 'blue' }
        ]}
      />

      {/* CTA */}
      <section className="py-20 px-4 bg-gradient-to-r from-amber-600 to-amber-700 text-white">
        <div className="container mx-auto text-center max-w-4xl">
          <h2 className="text-4xl font-bold mb-6">Get Complete SME Support Today</h2>
          <p className="text-xl text-amber-100 mb-8 max-w-2xl mx-auto">
            Simplify your accounting, tax, and compliance with one reliable partner.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-amber-700 hover:bg-gray-100 px-8 py-6 text-lg" data-testid="sme-cta-consultation-btn">
                Book Your FREE Consultation
              </Button>
            </Link>
            <a href="https://wa.me/971501219341" target="_blank" rel="noopener noreferrer" data-testid="sme-whatsapp-btn">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                WhatsApp Us
              </Button>
            </a>
          </div>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-6 text-amber-100">
            <a href="tel:+971509987431" className="flex items-center gap-2 hover:text-white transition-colors" data-testid="sme-phone-1">
              <Phone size={18} />
              <span>+971 50 998 7431</span>
            </a>
            <a href="https://wa.me/971501219341" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white transition-colors" data-testid="sme-phone-2">
              <MessageSquare size={18} />
              <span>WhatsApp +971 50 121 9341</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SMEBusinessServicesUAE;
